import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, MenuItem, Select, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "components/basic/button";
import ConfirmDialog from "components/basic/confirmDialog";
import Typography from "components/basic/typography";
import { TicketStatus } from "generated/graphql";

import TicketStatusBadge from "../../../TicketStatusBadge";
import { CATEGORY_LABELS, STATUS_LABELS } from "../../../../constants";
import { requesterLabel } from "../../../../helpers";
import { SupportTicket } from "../../../../types";

interface TicketHeaderProps {
  ticket: SupportTicket;
  asAdmin: boolean;
  onBack: () => void;
  onStatusChange: (status: TicketStatus) => Promise<boolean>;
  onDelete?: () => Promise<void>;
}

const TicketHeader: React.FC<TicketHeaderProps> = ({
  ticket,
  asAdmin,
  onBack,
  onStatusChange,
  onDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("support");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const resolved = ticket.status === TicketStatus.Resolved;

  const changeStatus = async (status: TicketStatus) => {
    setBusy(true);
    await onStatusChange(status);
    setBusy(false);
  };

  const handleDelete = async () => {
    setBusy(true);
    await onDelete?.();
    setBusy(false);
    setConfirmDelete(false);
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1}
      px={2}
      py={1}
      borderBottom={`1px solid ${theme.palette.divider}`}
    >
      <IconButton onClick={onBack} aria-label={t("detail.back")}>
        <ArrowBackIcon />
      </IconButton>

      <Box flexGrow={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="h4" style={{ fontWeight: "bold" }}>
            #{ticket.id} · {ticket.subject}
          </Typography>
          <TicketStatusBadge status={ticket.status} />
        </Box>
        <Typography
          variant="caption"
          style={{ color: theme.palette.text.secondary }}
        >
          {t(CATEGORY_LABELS[ticket.category])}
          {asAdmin && ` · ${requesterLabel(ticket)} · ${ticket.requesterEmail}`}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
        {asAdmin ? (
          <>
            <Select
              size="small"
              value={ticket.status}
              disabled={busy}
              onChange={(e) => changeStatus(e.target.value as TicketStatus)}
              aria-label={t("detail.status_label")}
            >
              {Object.values(TicketStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {t(STATUS_LABELS[status])}
                </MenuItem>
              ))}
            </Select>
            {onDelete && (
              <IconButton
                onClick={() => setConfirmDelete(true)}
                aria-label={t("detail.delete")}
                title={t("detail.delete")}
              >
                <DeleteOutlineIcon />
              </IconButton>
            )}
          </>
        ) : (
          <Button
            size="small"
            loading={busy}
            onClick={() =>
              changeStatus(resolved ? TicketStatus.Open : TicketStatus.Resolved)
            }
          >
            {t(resolved ? "detail.reopen" : "detail.resolve")}
          </Button>
        )}
      </Box>

      {confirmDelete && (
        <ConfirmDialog
          title={t("detail.delete_title")}
          body={t("detail.delete_body")}
          confirmLabel={t("detail.delete_confirm")}
          destructive
          loading={busy}
          onConfirm={handleDelete}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </Box>
  );
};

export default TicketHeader;
